/**
 * CalibrationTakesPanel — coach-facing calibration takes UI (T6, B-2026-0126).
 *
 * Rendered inside TemplateDetails.tsx for template owners/writers only.
 *
 * Composes:
 *  - CalibrationTakeUploader  — file pick + upload + trigger processing
 *  - CalibrationTakeList      — list takes with per-take polling
 *  - CalibrationStatusCard    — plain-language summary (no σ/k values)
 *  - StrictnessSelector       — Lenient / Standard / Strict dial (ships in this slice)
 *
 * Collapse state: panel is collapsed by default when there are 0 takes
 * (reduces clutter for first-time use). Expands automatically once a take exists.
 *
 * Strictness dial:
 *   lenient / standard / strict — persisted via
 *   PATCH /api/templates/{id}/calibration-takes/calibration-strictness and applied
 *   as a bounded ±15% nudge on the CALIBRATED tolerance only (no effect without
 *   take-derived calibration). Initial value comes from the calibration summary.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Alert, Spinner } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import CalibrationTakeUploader from './CalibrationTakeUploader';
import CalibrationTakeList from './CalibrationTakeList';
import CalibrationStatusCard from './CalibrationStatusCard';
import CoachFeedbackList from './CoachFeedbackList';
import StrictnessSelector, { type StrictnessLevel } from './StrictnessSelector';
import PricingCoachLimitsFields from './PricingCoachLimitsFields';
import RuleCompilerPanel from './RuleCompilerPanel';
import {
  useCalibrationTakeList,
  useCalibrationStatus,
  useDeleteCalibrationTake,
} from '@/hooks/useCalibrationTakes';
import { ChevronDownIcon, ChevronRightIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { saveCalibrationStrictness } from '@/services/templateApi';
import { useAdaptiveGuide } from '@/contexts/AdaptiveGuideContext';

interface CalibrationTakesPanelProps {
  templateId: string;
}

export default function CalibrationTakesPanel({ templateId }: CalibrationTakesPanelProps) {
  const { data: listData, isLoading, error } = useCalibrationTakeList(templateId);
  const { data: summaryData } = useCalibrationStatus(templateId);
  const deleteMutation = useDeleteCalibrationTake(templateId);
  const { isGuideOpen, setGuideOpen } = useAdaptiveGuide();

  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [strictness, setStrictness] = useState<StrictnessLevel>('standard');
  const [strictnessError, setStrictnessError] = useState<string | null>(null);
  const [strictnessInitialized, setStrictnessInitialized] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const takes = listData?.takes ?? [];
  const calSummary = listData?.calibration_summary ?? summaryData ?? null;

  const nGood = takes.filter((t) => t.label === 'good' && t.status !== 'failed').length;
  const nBad = takes.filter((t) => t.label === 'bad' && t.status !== 'failed').length;
  const nFail = takes.filter((t) => t.label === 'fail' && t.status !== 'failed').length;
  const nCompletedGood = takes.filter(
    (t) => t.label === 'good' && t.status === 'completed',
  ).length;

  // Auto-expand when a take is added
  useEffect(() => {
    if (takes.length > 0) {
      setIsCollapsed(false);
    }
  }, [takes.length]);

  // Initialize the strictness dial from the backend once (summary.strictness).
  useEffect(() => {
    if (!strictnessInitialized && calSummary?.strictness) {
      const s = calSummary.strictness;
      if (s === 'lenient' || s === 'standard' || s === 'strict') {
        setStrictness(s);
      }
      setStrictnessInitialized(true);
    }
  }, [calSummary?.strictness, strictnessInitialized]);

  const handleTakeQueued = (_takeId: string) => {
    // List query is invalidated by the mutation; nothing extra needed here.
  };

  const handleDeleteTake = async (takeId: string) => {
    setIsDeletingId(takeId);
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(takeId);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to remove take. Please try again.',
      );
    } finally {
      setIsDeletingId(null);
    }
  };

  // Count badge label
  const takeBadgeLabel =
    takes.length === 0
      ? 'No takes'
      : [
          `${nGood} correct`,
          nBad > 0 ? `${nBad} flawed` : null,
          nFail > 0 ? `${nFail} disqualifying` : null,
        ]
          .filter(Boolean)
          .join(', ');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center justify-between flex-1 min-w-0 text-left group"
            onClick={() => setIsCollapsed((c) => !c)}
            aria-expanded={!isCollapsed}
            aria-controls="calibration-takes-panel-content"
          >
            <div className="flex items-center gap-3">
              <CardTitle>Calibration Takes</CardTitle>
              <Badge
                variant={nCompletedGood >= 2 ? 'success' : 'secondary'}
                size="sm"
              >
                {takeBadgeLabel}
              </Badge>
            </div>
            <span className="text-text-muted group-hover:text-text-secondary transition-colors">
              {isCollapsed ? (
                <ChevronRightIcon className="h-5 w-5" />
              ) : (
                <ChevronDownIcon className="h-5 w-5" />
              )}
            </span>
          </button>

          {/* Guide toggle — calibration-upload-guidance. A sibling of (not nested
              inside) the collapse-toggle button above; reopens the detailed guide
              drawer regardless of the panel's own collapsed state. */}
          {!isGuideOpen && (
            <button
              type="button"
              data-testid="calibration-guide-button"
              onClick={() => setGuideOpen(true)}
              aria-label="Open calibration guide"
              className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--color-surface-raised-3) 80%, transparent)',
                border: '1px solid color-mix(in srgb, var(--color-accent-cyan) 40%, transparent)',
                color: 'var(--color-accent-cyan)',
              }}
            >
              <QuestionMarkCircleIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              Guide
            </button>
          )}
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent id="calibration-takes-panel-content" className="space-y-6">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Spinner size="md" label="Loading takes..." />
            </div>
          )}

          {/* Error state */}
          {error && (
            <Alert variant="error">
              Could not load calibration takes.{' '}
              {error instanceof Error ? error.message : 'Please refresh and try again.'}
            </Alert>
          )}

          {!isLoading && !error && (
            <>
              {/* Condensed guide copy — the "short" tier of the two-tier calibration
                  example-video guidance (calibration-upload-guidance). The detailed
                  tier lives behind the drawer (TemplateGuideOverlay); this is a
                  one-line summary, not a restatement of it — see
                  data/templateGuideContent.ts for the full copy and the rationale
                  for keeping the two tiers distinct rather than duplicated. */}
              <div className="flex items-start justify-between gap-3 rounded-lg border border-border-subtle bg-dark-elevated-2 px-3 py-2">
                <p className="text-xs text-text-secondary">
                  These examples define where the scoring lines fall — the clips
                  closest to your judgment call teach the most.
                </p>
                <button
                  type="button"
                  data-testid="calibration-guide-learn-more"
                  onClick={() => setGuideOpen(true)}
                  className="flex-shrink-0 text-xs font-medium text-accent hover:underline"
                >
                  Learn more
                </button>
              </div>

              {/* Calibration status — "What the system reports back".
                  coach_feedback (what the uploaded example videos taught the
                  scoring system) renders first — single-voice ordering, see
                  CoachFeedbackList's docstring — followed by
                  CalibrationStatusCard, which now collapses the legacy
                  coach_messages list behind its own "Details" disclosure and
                  self-suppresses the same-person Alert when coach_feedback
                  already covers that topic (see that file). Both live inside
                  this guide-drawer scroll-spy anchor. */}
              <div data-guide-section="calibration-results" className="space-y-3">
                <CoachFeedbackList
                  items={calSummary?.coach_feedback}
                  feedbackTakeIds={calSummary?.feedback_take_ids}
                  liveTakeIds={takes.map((t) => t.take_id)}
                />
                <CalibrationStatusCard
                  templateId={templateId}
                  summary={calSummary}
                  nCompletedGood={nCompletedGood}
                />
              </div>

              {/* Existing takes */}
              {takes.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-2">Uploaded takes</p>
                  {deleteError && (
                    <Alert variant="error" className="mb-3">
                      {deleteError}
                    </Alert>
                  )}
                  <CalibrationTakeList
                    templateId={templateId}
                    takes={takes}
                    onDeleteTake={handleDeleteTake}
                    isDeletingId={isDeletingId}
                  />
                </div>
              )}

              {/* Upload section */}
              <div>
                <p className="text-sm font-medium text-text-secondary mb-2">Add a take</p>
                <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-4">
                  <CalibrationTakeUploader
                    templateId={templateId}
                    nGood={nGood}
                    nBad={nBad}
                    nFail={nFail}
                    onTakeQueued={handleTakeQueued}
                  />
                </div>
              </div>

              {/* Strictness dial — shown once calibration is active (>=2 completed good takes).
                  Persists via PATCH .../calibration-takes/calibration-strictness; applied as a
                  bounded ±15% nudge on the calibrated tolerance. */}
              {nCompletedGood >= 2 && (
                <div>
                  <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-4">
                    <StrictnessSelector
                      showPricingEffect
                      value={strictness}
                      onChange={(level) => {
                        setStrictness(level);
                        setStrictnessError(null);
                        saveCalibrationStrictness(templateId, level).catch(() => {
                          setStrictnessError(
                            'Could not save the strictness setting — please try again.',
                          );
                        });
                      }}
                    />
                    <p className="text-xs text-text-muted mt-3">
                      Strictness adjusts how precisely athletes must replicate this template.
                      It does not change what counts as a gate failure.
                    </p>
                    {strictnessError && (
                      <p className="text-xs text-red-400 mt-2">{strictnessError}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Coach-typed pricing limits (Goal F, EXP-010 A7b) — shown
                  independently of the strictness dial's own nCompletedGood
                  gate: the body-position field is always available (L-2),
                  even on a day-one template with zero completed takes. */}
              <div>
                <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-4">
                  <PricingCoachLimitsFields
                    templateId={templateId}
                    pricingCoachLimits={calSummary?.pricing_coach_limits}
                    pricingLearnedLimits={calSummary?.pricing_learned_limits}
                  />
                </div>
              </div>

              {/* Compile coach rules (coach-knowledge-codification epic, Phase 3).
                  Self-gates visibility: renders nothing when there is no reason_text
                  on a bad/fail take, no common_mistakes, and no prior compile run. */}
              <RuleCompilerPanel templateId={templateId} takes={takes} />
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
